import React, { createContext, useContext, useState } from "react"

import useStrings from "../localization/useStrings"
import native from "../native/native"
import { formatCurrency } from "../utils/utils"
import { useAppState } from "./appStateContext"
import { PostPurchaseProvider } from "./postPurchaseContext"
import useQueryParams from "../hooks/useQueryParams"
import { Plan, Product } from "../appData/productData"
import PostPurchaseHandler from "../models/postPurchaseHandler"

export interface PaywallItem {
  title: string
  monthlyPrice: string
  yearlyPrice?: string
  discount?: string
  selected: boolean
}

interface State {
  monthlyItem: PaywallItem
  yearlyItem: PaywallItem
  ctaTitle: string
  ultimateSelected: boolean
  beforeYouGoEnabled: boolean
  toggleableFreeTrial: boolean
  freeTrialToggled: boolean
  trialFooterEnabled: boolean
  trialDays: number
  selectedProduct: Product
  selectedPlan: Plan
}

interface Actions {
  setYearlySelected: (selected: boolean) => void
  purchaseCurrent: () => void
  purchaseYearly: () => void
  purchaseNoTrialMonthly: () => void
  purchaseNoTrialYearly: () => void
  purchaseProduct: (product: Product) => void
  setUltimateSelected: (selected: boolean) => void
  setFreeTrialToggled: (toggled: boolean) => void
  purchaseCurrentOffer: () => void
}

interface Context {
  state: State
  actions: Actions
}

const PurchaseContext = createContext<Context | null>(null)

const PurchasProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const purchaseCurrent = () => {
    if (trialFooterEnabled && !yearlySelected) {
      native.subscribeWithId("ppv_no_trial_15_monthly_mid_2022")
      return
    }

    if (toggleableFreeTrial && !freeTrialToggled) {
      if (yearlySelected) {
        native.subscribeWithId("ppv_no_trial_36_yearly_mid_2022")
      } else {
        native.subscribeWithId("ppv_no_trial_15_monthly_mid_2022")
      }
      return
    }

    const originalProduct = yearlySelected
      ? plan.yearlyProduct
      : plan.monthlyProduct
    if (
      queryParams.forceNoTrialUltimate &&
      ultimateSelected &&
      originalProduct.hasTrial
    ) {
      if (yearlySelected) {
        native.subscribeWithId("ppv_no_trial_36_yearly_mid_2022_ultimate")
      } else {
        native.subscribeWithId("ppv_no_trial_15_monthly_mid_2022_ultimate")
      }
      return
    }

    let endFlow = true

    if (queryParams.postPurchaseFlow) {
      endFlow =
        new PostPurchaseHandler(selectedProduct, plan, ultimatePlan)
          .shouldShowFlow == false
    }

    // Use product from longest receipt if available and ultimate is not selected
    const longestReceipt = productData.longestUsedSubscription
    const productToPurchase =
      longestReceipt && !ultimateSelected
        ? yearlySelected
          ? longestReceipt.collection.yearlyProduct
          : longestReceipt.collection.monthlyProduct
        : selectedProduct

    native.subscribe(productToPurchase, endFlow)

    if (queryParams.debug && !endFlow) {
      ;(window as any).purchaseMade()
    }
  }

  const purchaseCurrentOffer = () => {
    switch (queryParams.specialOffer) {
      case "secondTrial":
        if (queryParams.useYearlyForPromoTrial) {
          native.subscribeWithPromoFreeTrial(plan.yearlyProduct)
        } else {
          if (productData.activeSubscription?.period === "monthly") {
            native.subscribeWithPromoFreeTrial(plan.monthlyProduct)
          } else {
            native.subscribeWithPromoFreeTrial(plan.yearlyProduct)
          }
        }

        break
      case "expiring":
        native.subscribeWithDiscount(plan.yearlyProduct)
        break
    }
  }

  const purchaseYearly = () => {
    native.subscribe(premiumPlan.yearlyProduct)
  }

  const purchaseNoTrialMonthly = () => {
    if (premiumPlan.yearlyProduct.productId == "ppv_pro_yearly_3_month_trial") {
      native.subscribeWithId("ppv_no_trial_15_monthly_mid_2022")
    } else {
      native.subscribeWithId("ppv_pro_monthly_no_trial")
    }
  }

  const purchaseNoTrialYearly = () => {
    native.subscribeWithId("ppv_no_trial_36_yearly_mid_2022")
  }

  const purchaseProduct = (product: Product) => {
    native.subscribe(product)
  }

  const productData = useAppState().state.productData!
  const strings = useStrings()

  const queryParams = useQueryParams()
  const hasUltimateQueryParams =
    queryParams.isUltimate || queryParams.ultimateOnly

  let defaultToUltimate =
    hasUltimateQueryParams && productData.ultimatePaywallGlobalApplies

  defaultToUltimate =
    defaultToUltimate ||
    productData.activeSubscription?.productId.includes("ultimate") == true ||
    (queryParams.defaultToUltimate && queryParams.showUltimateInExpiration)

  const [yearlySelected, setYearlySelectedRaw] = useState(true)

  const setYearlySelected = (selected: boolean) => {
    if (selected != yearlySelected) {
      setYearlySelectedRaw(selected)
      native.logMixpanelEvent("html_pricing_toggle", {
        plan: selected ? "yearly" : "monthly",
      })
    }
  }
  const [ultimateSelectedState, setUltimateSelectedState] = useState(
    productData.isUltimatePlacement || defaultToUltimate,
  )

  const [freeTrialToggled, setFreeTrialToggledRaw] = useState(true)

  const setFreeTrialToggled = (newValue: boolean) => {
    setFreeTrialToggledRaw(newValue)
    native.logMixpanelEvent("html_trial_toggled", { value: newValue })
  }

  const ultimateSelected =
    ultimateSelectedState && productData.ultimatePaywallGlobalApplies

  const setUltimateSelected = (selected: boolean) => {
    if (selected != ultimateSelectedState) {
      setUltimateSelectedState(selected)
      native.logMixpanelEvent("html_plan_toggle", {
        plan: selected ? "ultimate" : "premium",
      })
    }
  }

  if (ultimateSelected) {
    document.documentElement.setAttribute("paywall-style", "ultimate")
  } else {
    document.documentElement.setAttribute("paywall-style", "premium")
  }

  const ultimateActiveSubscription =
    productData.activeSubscription?.productId.includes("ultimate") == true

  const plan =
    ultimateActiveSubscription || ultimateSelected
      ? productData.ultimate
      : productData.premium
  const premiumPlan = productData.premium
  const ultimatePlan = productData.ultimate
  const discountPlan = queryParams.ultimateOnly ? ultimatePlan : premiumPlan

  const locale = plan.yearlyProduct.priceLocale

  const yearlyPrice = strings.pricePerYear(
    formatCurrency(plan.yearlyProduct.price, locale),
  )
  const yearlyPerMonthPrice = strings.pricePerMonth(
    formatCurrency(plan.yearlyProduct.price / 12, locale),
  )
  const monthlyPrice = strings.pricePerMonth(
    formatCurrency(plan.monthlyProduct.price, locale),
  )
  const discountRatio =
    discountPlan.yearlyProduct.price / (discountPlan.monthlyProduct.price * 12)
  const discount = Math.round((1 - discountRatio) * 100)

  const monthlyItem: PaywallItem = {
    title: strings.monthlyPlan,
    monthlyPrice,
    selected: !yearlySelected,
  }
  const yearlyItem: PaywallItem = {
    title: strings.yearlyPlan,
    monthlyPrice: yearlyPerMonthPrice,
    yearlyPrice,
    selected: yearlySelected,
    discount: `${strings.discount(discount)}`,
  }

  if (productData.isDebug) {
    monthlyItem.yearlyPrice = ""
    monthlyItem.monthlyPrice = ""
    yearlyItem.monthlyPrice = ""
    yearlyItem.yearlyPrice = ""
    yearlyItem.discount = ""
  }

  let selectedProduct = yearlySelected
    ? plan.yearlyProduct
    : plan.monthlyProduct

  if (
    queryParams.forceNoTrialUltimate &&
    ultimateSelected &&
    selectedProduct.hasTrial
  ) {
    selectedProduct = {
      ...selectedProduct,
      hasTrial: false,
      introPricing: undefined,
    }
  }

  let hasTrial = selectedProduct.hasTrial && plan.hasTrial

  const toggleableFreeTrial = queryParams.toggleFreeTrial && hasTrial

  if (toggleableFreeTrial) {
    hasTrial = hasTrial && freeTrialToggled
  }

  const trialDays = selectedProduct.introPricing?.period ?? 7
  const trialFooterEnabled = queryParams.trialFooter && plan.hasTrial

  const ctaTitle = hasTrial ? strings.freeTrial : strings.startPlan

  const beforeYouGoEnabled =
    queryParams.beforeYouGo && plan.yearlyProduct.hasTrial

  const state = {
    monthlyItem,
    yearlyItem,
    ctaTitle,
    ultimateSelected,
    beforeYouGoEnabled,
    toggleableFreeTrial,
    freeTrialToggled,
    trialFooterEnabled,
    trialDays,
    selectedProduct,
    selectedPlan: plan,
  }
  const actions = {
    setYearlySelected,
    purchaseCurrent,
    setUltimateSelected,
    purchaseYearly,
    setFreeTrialToggled,
    purchaseProduct,
    purchaseCurrentOffer,
    purchaseNoTrialMonthly,
    purchaseNoTrialYearly,
  }

  return (
    <PurchaseContext.Provider value={{ state, actions }}>
      <PostPurchaseProvider>{children}</PostPurchaseProvider>
    </PurchaseContext.Provider>
  )
}

const usePurchase = () => {
  const context = useContext(PurchaseContext)

  if (!context) {
    throw new Error(
      "usePurchase has to be used within <PurchaseContext.Provider>",
    )
  }

  return context
}

export { PurchasProvider, PurchaseContext, usePurchase }
